/**
 * [Phase 2c-B §4] SnakeDraftContext - WebSocket connection + state for
 * snake-format leagues. Parallel to DraftContext (which is locked to auction).
 *
 * Shares the same /api/ws/draft/{league_id} URL but interprets messages
 * relevant to snake state (DRAFT_STARTED, PICK_MADE, DRAFT_COMPLETE,
 * DRAFT_PAUSED, DRAFT_RESUMED, STATE_SNAPSHOT). The backend dispatches the
 * correct engine based on League.settings.draft_format, so the client just
 * needs to interpret the snake-shaped state dicts.
 *
 * [Phase 2c.6-A.1.10 ARCHITECTURAL PIVOT - auction-pattern client synthesis]
 * Snake live-feed Adam content is now CLIENT-SYNTHESIZED from PICK_MADE WS
 * frames (mirroring auction's DraftContext `sayAdam` pattern). The prior
 * backend-write → ACTIVITY_WRITTEN broadcast → REST hydration pipeline failed
 * 5 consecutive sub-checkpoints in the wild (TD-44 / "iter green, founder
 * broken"). Removing the pipeline removes the failure surface entirely.
 *
 * Removed:
 *   - REST hydration on mount + WS reconnect
 *   - 60s hydration heartbeat
 *   - 30s message-silence forced reconnect
 *   - ACTIVITY_WRITTEN handler (no more rows arrive via WS for snake)
 *
 * Added:
 *   - pushAdamLine(activityType, text) helper - mirrors auction's sayAdam
 *   - PICK_MADE handler synthesizes text via utils/snakeTemplates.js
 *   - ROUND_START synthesized on round boundary via detectRoundBoundary
 *   - latestAdamMessage is the DISPLAY channel (SnakeCenterSurface);
 *     audio is enqueued per line into the auctioneerAudio queue
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { getToken, authHeaders } from "./AuthContext";
import {
  pickMadeTemplate,
  roundStartTemplate,
  detectRoundBoundary,
} from "../utils/snakeTemplates";
import { enqueue, setAdamLeague } from "../audio/auctioneerAudio";
import { diag, diagError, setDiagContext } from "../utils/diag";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SnakeDraftContext = createContext(null);

// [HF3 P0-2] Cross-tab + cross-surface recap-dismiss persistence. The
// DraftEndRecapModal mounts on multiple surfaces (SnakeDraftRoom +
// SnakeStandaloneDraftBoard which open in separate tabs with separate
// providers). Founder spec: "if user dismisses on one surface, modal stays
// dismissed on the other." Backed by localStorage; cross-tab via 'storage'
// event.
const RECAP_DISMISS_KEY = (lid) => `pgf_recap_dismissed:${lid}`;

/**
 * @param silent - when true this provider NEVER enqueues Adam audio. The
 * standalone board mounts its own provider, so without this every board tab
 * spoke over the draft room / lobby (live bug). The board is a visual surface;
 * Adam plays from the tab the manager is actually sitting in.
 */
export function SnakeDraftProvider({ leagueId, children, silent = false }) {
  const [draftState, setDraftState] = useState(null);
  // [ADAM AUDIO batch 2] Scope live synth to this league so /tts/speak
  // resolves the league's chosen voice instead of defaulting to Voice 1.
  useEffect(() => { setAdamLeague(leagueId); }, [leagueId]);
  const [isConnected, setIsConnected] = useState(false);
  // [BOARD MIRROR] Pre-draft progress relayed by the commissioner's lobby tab
  // (the tab that owns Adam, so it sets the pace) and fanned out over the
  // existing draft socket. Absolute values, never deltas, so a dropped or
  // late frame cannot desync a mirroring board.
  // Shape: { phase: "REVEAL"|"COUNTDOWN"|"LIVE", revealed_count, countdown, order }
  const [predraftProgress, setPredraftProgress] = useState(null);
  const [wsUser, setWsUser] = useState(null);
  // [A.1.10] Client-synthesized snake feed rows. Shape kept identical to
  // auction's DraftContext.activityVoice so <LiveActivityFeed> renders both
  // formats from the same component.
  const [activityVoice, setActivityVoice] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  // [Phase 2c.6-C] CHAT_REJECTED transient state for inline UX banner.
  const [chatRejected, setChatRejected] = useState(null);
  const clearChatRejected = useCallback(() => setChatRejected(null), []);
  const [bridgeRecapUrl, setBridgeRecapUrl] = useState(null);
  const [mockDraftCompleteEvent, setMockDraftCompleteEvent] = useState(null);
  // Most recent line for the auto-TTS hook (useSnakeAdamTTS).
  const [latestAdamMessage, setLatestAdamMessage] = useState(null);

  // [HF3 P0-2] Recap-dismiss state hoisted from per-surface useState so
  // multiple surfaces (SnakeDraftRoom + SnakeStandaloneDraftBoard) stay in
  // sync via localStorage + 'storage' event listener.
  const [recapDismissed, setRecapDismissedRaw] = useState(() => {
    try { return !!leagueId && localStorage.getItem(RECAP_DISMISS_KEY(leagueId)) === "1"; }
    catch { return false; }
  });
  const dismissRecap = useCallback(() => {
    setRecapDismissedRaw(true);
    try { if (leagueId) localStorage.setItem(RECAP_DISMISS_KEY(leagueId), "1"); } catch {}
  }, [leagueId]);
  useEffect(() => {
    if (!leagueId) return;
    const handler = (e) => {
      if (e.key === RECAP_DISMISS_KEY(leagueId) && e.newValue === "1") {
        setRecapDismissedRaw(true);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [leagueId]);

  // [HF4.5] Per-round ROUND_STARTED event. Fires for EVERY round boundary
  // including the implicit round-1 event broadcast at start_draft and the
  // per-boundary broadcasts inside submit_pick (engines/snake_draft.py).
  // Round 1 carries no visual modal (the 10s lobby DraftCountdown covers
  // that beat); rounds 2-N drive the BetweenRoundsCountdownModal on the
  // standalone draft board only. `ts` enables late-join staleness checks.
  const [roundStartedEvent, setRoundStartedEvent] = useState(null);
  const consumeRoundStartedEvent = useCallback(() => {
    setRoundStartedEvent(null);
  }, []);

  // [HF4.5] Round-1 Adam welcome derivation. The DRAFT_STARTED WS frame
  // fires while the user is still on the lobby page (the lobby's 10s
  // DraftCountdown is playing). By the time the user lands in the snake
  // draft room and this WS connects, that frame is gone - replaying never
  // happens. So the existing `pushAdamLine("DRAFT_STARTED_WELCOME", ...)`
  // handler tied to the DRAFT_STARTED case never executes for snake.
  //
  // Backstop: when the first STATE_SNAPSHOT shows (DRAFTING, round=1,
  // picks=0), derive the welcome line locally. Per-league localStorage
  // flag prevents replays on reload/HMR.
  useEffect(() => {
    if (!leagueId || !draftState) return;
    const ds = draftState;
    const isRound1PreFirstPick =
      ds.state === "DRAFTING" &&
      (ds.current_round ?? 1) === 1 &&
      (ds.total_picks_made ?? 0) === 0;
    if (!isRound1PreFirstPick) return;
    const key = `pgf_round1_welcome_played:${leagueId}`;
    try {
      if (localStorage.getItem(key) === "1") return;
      localStorage.setItem(key, "1");
    } catch {
      /* ignore localStorage failures */
    }
    pushAdamLine(
      "DRAFT_STARTED_WELCOME",
      "Welcome to your draft. Adam here. Round 1 starts now.",
    );
    // pushAdamLine is stable (useCallback []); leagueId triggers the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    leagueId,
    draftState?.state,
    draftState?.current_round,
    draftState?.total_picks_made,
  ]);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  // [A.1.10] Track draftState in a ref so the WS handler can read team_count
  // + roster_size synchronously without re-binding handleMessage on every
  // state change.
  const draftStateRef = useRef(null);
  useEffect(() => { draftStateRef.current = draftState; }, [draftState]);

  /**
   * Push an Adam-voice line into the live feed + trigger auto-TTS.
   * Mirrors auction's pushAuctioneerLine pattern (DraftContext.sayAdam).
   * voice_source='auctioneer' because this is client-synthesized content -
   * the same enum value auction uses for the marquee strings it injects.
   *
   * [ADAM AUDIO foundation] Audio is enqueued HERE, per line. It used to
   * ride on `latestAdamMessage`, a single latest-value slot: a PICK_MADE
   * that crossed a round boundary pushed the pick line and the ROUND_START
   * line in the SAME WS tick, React batched both setStates, and the TTS
   * hook only ever observed the last one - so one of the two lines was
   * destroyed before any audio layer saw it. That was the second cause of
   * the clipped round countdown. `latestAdamMessage` is now the DISPLAY
   * channel only (SnakeCenterSurface reads `.text`, which is why it is an
   * object).
   */
  const pushAdamLine = useCallback((activityType, text) => {
    if (!text) return;
    const id = `adam-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    if (!silent) enqueue({ id, group: activityType, text });
    setActivityVoice((prev) => ({
      ...prev,
      [id]: {
        voiceText: text,
        voiceSource: "auctioneer",
        activityType,
        pickId: null,
        createdAt: new Date().toISOString(),
      },
    }));
    setLatestAdamMessage({ id, text });
  }, [silent]);

  const connect = useCallback(() => {
    const token = getToken();
    if (!token || !leagueId) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backendUrl.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/draft/${leagueId}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      diag("ws.open", { leagueId });
      setIsConnected(true);
      clearTimeout(reconnectRef.current);
      const ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "PING" }));
      }, 25000);
      ws._pingInterval = ping;
    };
    ws.onmessage = (e) => {
      try { handleMessage(JSON.parse(e.data)); } catch (_e) { /* drop */ }
    };
    ws.onclose = (e) => {
      clearInterval(ws._pingInterval);
      setIsConnected(false);
      // A silently-closing socket looks exactly like "the draft is frozen":
      // state stops updating, so nobody appears to be on the clock.
      diagError("ws.close", { code: e?.code ?? null, reason: String(e?.reason || "").slice(0, 120) });
      reconnectRef.current = setTimeout(connect, 3000);
    };
    ws.onerror = () => {
      diagError("ws.error", { leagueId });
      ws.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  const handleMessage = (msg) => {
    const { type, data } = msg;

    // FULL_STATE_TYPES carry the snake engine's to_dict() shape.
    const FULL_STATE_TYPES = new Set([
      "STATE_SNAPSHOT", "DRAFT_STARTED", "PICK_MADE", "PICK_UNDONE",
      "DRAFT_COMPLETE", "DRAFT_PAUSED", "DRAFT_RESUMED",
    ]);
    if (data && FULL_STATE_TYPES.has(type)) setDraftState(data);

    switch (type) {
      case "STATE_SNAPSHOT":
        if (msg.user) setWsUser(msg.user);
        setDiagContext({ league_id: leagueId, user_id: msg.user?.id || null });
        diag("ws.snapshot", {
          state: data?.state ?? null,
          picker: data?.current_picker_id ?? null,
          me: msg.user?.id ?? null,
          order_len: (data?.draft_order || []).length,
          picks: data?.total_picks_made ?? null,
          timer_remaining: data?.timer_remaining ?? null,
          predraft_progress: data?.predraft_progress ?? null,
        });
        // Late-opened board catches up mid-reveal / mid-countdown instead of
        // sitting blank until the next event.
        if (data?.predraft_progress) setPredraftProgress(data.predraft_progress);
        break;
      case "PREDRAFT_PROGRESS":
        setPredraftProgress(data || null);
        break;
      case "DRAFT_STARTED": {
        // [Phase 2c.6-A.2.1 D-7 / RL-28] Verbatim founder welcome line.
        // Client-synthesized (zero LLM, deterministic). Fires for both Live
        // and Mock snake drafts.
        pushAdamLine(
          "DRAFT_STARTED_WELCOME",
          "Welcome to your draft. Adam here. Round 1 starts now."
        );
        break;
      }
      // [TD-89 v2 Verdict 8.A] Lifecycle events - re-fetch league.
      case "DRAFT_ABANDONED":
        try { window.dispatchEvent(new CustomEvent("pgf:draft-abandoned", { detail: data })); } catch {}
        break;
      case "MANAGERS_UPDATED":
        try { window.dispatchEvent(new CustomEvent("pgf:managers-updated", { detail: data })); } catch {}
        break;

      case "PICK_MADE":
      case "DRAFT_COMPLETE": {
        // [A.1.10] Client-synthesize Adam's line for the just-made pick.
        // The engine broadcasts DRAFT_COMPLETE (NOT PICK_MADE) for the
        // FINAL pick of the draft (see engines/snake_draft.py:288). Both
        // frames carry the full snapshot in `data` with the just-made pick
        // at the tail of `data.pick_history`.
        const history = Array.isArray(data?.pick_history) ? data.pick_history : [];
        const pick = history.length ? history[history.length - 1] : null;
        if (pick) {
          const managers = data?.managers || {};
          const mgr = managers[pick.team_id] || {};
          const teamName = mgr.team_name || "Team";
          const player = pick.player || {};
          const playerName = (player.name || "the pick").trim();
          const nflTeamAbbr = (player.nfl_team || "").trim();

          const pickText = pickMadeTemplate(teamName, playerName, nflTeamAbbr);
          pushAdamLine("PICK_MADE", pickText);

          // Round-boundary detection: returns null on the final pick of the
          // draft (no next round), so DRAFT_COMPLETE frame won't fire one.
          // team_count is not in to_dict(); derive it from draft_order length
          // (or managers map count as fallback).
          const ds = data || draftStateRef.current || {};
          const teamCount =
            ds.team_count ||
            (Array.isArray(ds.draft_order) ? ds.draft_order.length : 0) ||
            (ds.managers ? Object.keys(ds.managers).length : 0);
          const nextRound = detectRoundBoundary(pick, teamCount, ds.roster_size);
          if (nextRound != null) {
            const roundText = roundStartTemplate(nextRound);
            pushAdamLine("ROUND_START", roundText);
          }
        }
        break;
      }
      case "PICK_UNDONE": {
        // [Undo item 5] Server already rolled the state back; the board reverts
        // from `data`. Adam must re-announce whoever is back on the clock, or
        // the room has no idea the turn moved backwards.
        const mgrs = data?.managers || {};
        const backOn = data?.current_picker_id;
        const teamName = (backOn && mgrs[backOn]?.team_name) || "The next team";
        const undonePlayer = (msg?.extra?.undone?.player?.name || "").trim();
        pushAdamLine(
          "PICK_UNDONE",
          undonePlayer
            ? `The commissioner has reversed the ${undonePlayer} pick. ${teamName} is on the clock!`
            : `The commissioner has reversed the last pick. ${teamName} is on the clock!`,
        );
        break;
      }

      case "CHAT_MESSAGE":
        // [Phase 2c.6-C] Dedupe by id when present.
        setChatMessages((prev) => {
          const incoming = data;
          if (incoming?.id && prev.some((m) => m.id === incoming.id)) return prev;
          return [...prev.slice(-199), incoming];
        });
        break;
      case "CHAT_REJECTED":
        setChatRejected({
          reason: data?.reason || "UNKNOWN",
          max_chars: data?.max_chars || 500,
          len: data?.len || 0,
          ts: Date.now(),
        });
        break;
      case "BRIDGE_COMPLETE":
        if (msg?.recap_url) setBridgeRecapUrl(msg.recap_url);
        break;
      case "MOCK_DRAFT_COMPLETE":
        setMockDraftCompleteEvent({
          league_type: msg?.league_type || null,
          at: Date.now(),
        });
        break;
      case "ROUND_STARTED": {
        // [HF4.5] Fires for EVERY round boundary. Round 1 carries no modal
        // (lobby's 10s DraftCountdown is the visual); rounds 2-N drive the
        // BetweenRoundsCountdownModal mounted on SnakeStandaloneDraftBoard.
        // Read round defensively - engine puts it in `extra`, but we also
        // accept `data.current_round` from the full state snapshot.
        const round =
          (msg?.extra && typeof msg.extra.round === "number" && msg.extra.round) ||
          (data?.current_round) ||
          1;
        // Only surface as an event for rounds 2+; round 1 is covered by the
        // lobby countdown + the Adam-welcome derivation effect above.
        if (round >= 2) {
          setRoundStartedEvent({ round, ts: Date.now() });
        }
        break;
      }
      case "ERROR":
        if (data?.message) {
          setChatMessages((prev) => [...prev.slice(-99), {
            sender_id: "system",
            sender_name: "System",
            text: `\u26A0\uFE0F ${data.message}`,
            is_system: true,
            timestamp: new Date().toISOString(),
          }]);
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const sendChat = useCallback((text) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "CHAT", data: { text } }));
    }
  }, []);

  // [TD-47.2b Gap A.1] submitPick - REST submit-pick action mirroring
  // auction's nominate() context contract. Owns request_id idempotency
  // token generation + busy-state lock + error surfacing.
  //
  // [Gap C.1] team_id maps to user.id today (snake server keys teams by
  // user_id). Future "1 user owns multiple teams" feature would refactor
  // this signature; for now the assumption is locked + tested.
  const submitPickBusyRef = useRef(false);
  // [Item 3] `forTeamId` is the commissioner proxy path ("Draft For LM"):
  // the pick is submitted FOR the team on the clock, not for the caller.
  // The server already grants the commissioner that authority and still
  // enforces that the team is actually on the clock.
  const submitPick = useCallback(async (playerId, forTeamId = null) => {
    if (!leagueId || !playerId) return { ok: false, error: "Missing args" };
    if (submitPickBusyRef.current) return { ok: false, error: "Pick in flight" };
    submitPickBusyRef.current = true;
    try {
      const teamId = forTeamId || wsUser?.id;
      if (!teamId) return { ok: false, error: "Not authenticated" };
      const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await axios.post(
        `${BACKEND}/api/draft/${leagueId}/submit-pick`,
        { team_id: teamId, player_id: playerId, request_id: requestId },
        { headers: authHeaders() },
      );
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail || "Pick rejected";
      return { ok: false, error: detail };
    } finally {
      submitPickBusyRef.current = false;
    }
  }, [leagueId, wsUser?.id]);

  // [Phase 2c.6-C] Chat history backfill on mount + reconnect.
  const hydrateChat = useCallback(async () => {
    if (!leagueId) return;
    try {
      const res = await axios.get(`${BACKEND}/api/chat/${leagueId}/draft-history?limit=100`, {
        headers: authHeaders(),
      });
      const msgs = Array.isArray(res?.data?.messages) ? res.data.messages : [];
      if (!msgs.length) return;
      setChatMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id).filter(Boolean));
        const merged = [...msgs.filter((m) => !seen.has(m.id)), ...prev];
        return merged.slice(-200);
      });
    } catch (_e) { /* non-fatal */ }
  }, [leagueId]);

  useEffect(() => {
    hydrateChat();
  }, [hydrateChat, isConnected]);

  return (
    <SnakeDraftContext.Provider value={{
      leagueId,
      draftState, isConnected, wsUser, chatMessages, activityVoice, bridgeRecapUrl,
      mockDraftCompleteEvent, setMockDraftCompleteEvent, sendChat, submitPick,
      chatRejected, clearChatRejected,
      latestAdamMessage, pushAdamLine,
      recapDismissed, dismissRecap,
      roundStartedEvent, consumeRoundStartedEvent,
      predraftProgress,
    }}>
      {children}
    </SnakeDraftContext.Provider>
  );
}

export const useSnakeDraft = () => useContext(SnakeDraftContext);
